import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SubscriptionPlanSelector from './SubscriptionPlanSelector';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrganisationRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [organisationId, setOrganisationId] = useState('');
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Company Info
    company_name: '',
    company_email: '',
    company_phone: '',
    street_address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    organisation_type: 'Limited Company',
    business_category: 'General Construction',
    
    // Admin User
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_phone_number: '',
    admin_address: '',
    admin_date_of_birth: '',
    // NOTE: Password fields removed - users set password via email link
    
    // Subscription
    subscription_tier: 'pro'
  });
  
  const [emailValidation, setEmailValidation] = useState({ valid: true, message: '' });
  const [companyEmailValidation, setCompanyEmailValidation] = useState({ valid: true, message: '', checking: false });
  const [adminEmailValidation, setAdminEmailValidation] = useState({ valid: true, message: '', checking: false });
  const [generatedUsername, setGeneratedUsername] = useState('');
  
  // Organisation types
  const organisationTypes = [
    'Limited Company',
    'Sole Trader',
    'Partnership',
    'Limited Liability Partnership (LLP)',
    'Public Limited Company (PLC)',
    'Non-Profit Organisation',
    'Other'
  ];
  
  // Fetch business categories from backend
  useEffect(() => {
    const fetchBusinessCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/business-categories`);
        const categories = response.data || [];
        setBusinessCategories(categories.map(cat => cat.name));
      } catch (error) {
        console.error('Error fetching business categories:', error);
        // Set default categories as fallback
        setBusinessCategories([
          'General Construction',
          'House Building',
          'Commercial Building',
          'Civil Engineering',
          'Groundworks',
          'Brickwork & Masonry',
          'Roofing',
          'Carpentry & Joinery',
          'Electrical Contracting',
          'Plumbing & Heating',
          'Plastering & Rendering',
          'Painting & Decorating',
          'Scaffolding',
          'Demolition',
          'Restoration & Refurbishment',
          'Fit-Out',
          'Property Maintenance',
          'Specialist Trade'
        ]);
      }
    };
    fetchBusinessCategories();
  }, []);
  
  // Countries (common ones)
  const countries = [
    'United Kingdom',
    'United States',
    'Ireland',
    'Canada',
    'Australia',
    'New Zealand',
    'Germany',
    'France',
    'Spain',
    'Italy',
    'Netherlands',
    'Belgium',
    'Switzerland',
    'Other'
  ];
  
  // Load subscription packages on mount
  useEffect(() => {
    loadSubscriptionPackages();
  }, []);
  
  const loadSubscriptionPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/subscription-packages`);
      setSubscriptionPackages(response.data);
      // Always default to Pro tier for trial period
      setFormData(prev => ({ ...prev, subscription_tier: 'pro' }));
    } catch (err) {
      console.error('Failed to load subscription packages:', err);
    }
  };
  
  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // NOTE: Password strength checker removed - users set password via email link

  // Real-time email validation on blur (checks availability via API)
  const handleEmailBlur = async (emailType, email) => {
    if (!email || !validateEmail(email)) return;
    
    const setValidation = emailType === 'organisation' ? setCompanyEmailValidation : setAdminEmailValidation;
    setValidation({ valid: true, message: '', checking: true });
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/public/validate-email`, {
        email: email,
        type: emailType
      });
      
      if (response.data.available) {
        setValidation({ valid: true, message: response.data.message, checking: false });
      } else {
        setValidation({ valid: false, message: response.data.message, checking: false });
      }
    } catch (err) {
      console.error('Email validation error:', err);
      setValidation({ valid: true, message: '', checking: false }); // Don't block on API error
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    
    // Real-time email validation
    if (name === 'admin_email' || name === 'company_email') {
      if (value && !validateEmail(value)) {
        setEmailValidation({ valid: false, message: 'Please enter a valid email address' });
      } else {
        setEmailValidation({ valid: true, message: '' });
      }
    }
  };
  
  const validateStep = (step) => {
    setError('');
    
    if (step === 1) {
      // Welcome step
      return true;
    }
    
    if (step === 2) {
      // Company information
      if (!formData.company_name.trim()) {
        setError('Company name is required');
        return false;
      }
      if (!formData.company_email.trim() || !validateEmail(formData.company_email)) {
        setError('Valid company email is required');
        return false;
      }
      if (!formData.company_phone.trim()) {
        setError('Company phone is required');
        return false;
      }
      if (!formData.street_address.trim() || !formData.city.trim() || !formData.postcode.trim()) {
        setError('Complete company address is required');
        return false;
      }
      return true;
    }
    
    if (step === 3) {
      // Admin user details
      if (!formData.admin_first_name.trim() || !formData.admin_last_name.trim()) {
        setError('Admin first name and last name are required');
        return false;
      }
      if (!formData.admin_email.trim() || !validateEmail(formData.admin_email)) {
        setError('Valid admin email is required');
        return false;
      }
      if (!formData.admin_phone_number.trim()) {
        setError('Admin phone number is required');
        return false;
      }
      if (!formData.admin_address.trim()) {
        setError('Admin address is required');
        return false;
      }
      if (!formData.admin_date_of_birth) {
        setError('Admin date of birth is required');
        return false;
      }
      // NOTE: Password validation removed - users set password via email link
      return true;
    }
    
    if (step === 4) {
      // Subscription selection
      if (!formData.subscription_tier) {
        setError('Please select a subscription plan');
        return false;
      }
      return true;
    }
    
    if (step === 5) {
      // Review step - all validation already done
      return true;
    }
    
    return true;
  };
  
  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      }
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };
  
  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Submit registration data (no password fields)
      const response = await axios.post(
        `${BACKEND_URL}/api/register-organisation`,
        formData
      );
      
      if (response.data.success) {
        setSuccess(true);
        setOrganisationId(response.data.organisation_id);
        setGeneratedUsername(response.data.admin_username);
        setCurrentStep(6); // Move to success step
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Registration failed. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Progress bar
  const progressPercentage = (currentStep / 6) * 100;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
      {/* Header with Logo and Cancel */}
      <div className="w-full bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="text-3xl">🏗️</div>
            <div>
              <h1 className="text-xl font-bold text-white">SiteOrbit</h1>
              <p className="text-sm text-gray-300">Organisation Registration</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Cancel</span>
          </button>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="w-full bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between mb-2 text-sm text-gray-300">
            <span>Step {currentStep} of 6</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500 shadow-lg shadow-purple-500/50"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Main Content Area - Full Screen */}
      <div className="flex-1 w-full overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Step Content */}
          <div className="p-8 md:p-12">
            {/* Step 1: Welcome */}
            {currentStep === 1 && (
              <div className="text-center py-8 animate-fade-in">
                <div className="text-7xl mb-8">🏗️</div>
                <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                  Welcome to SiteOrbit!
                </h1>
                <p className="text-2xl text-gray-700 mb-12 max-w-2xl mx-auto">
                  Let&apos;s get your construction business set up in just a few simple steps
                </p>
                <div className="grid md:grid-cols-3 gap-8 mb-12 text-left max-w-4xl mx-auto">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-200 hover:shadow-xl transition-all">
                    <div className="text-4xl mb-4">⚡</div>
                    <h3 className="font-bold text-gray-800 mb-3 text-lg">Quick Setup</h3>
                    <p className="text-gray-600">Complete registration in under 5 minutes</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl border border-blue-200 hover:shadow-xl transition-all">
                    <div className="text-4xl mb-4">🔒</div>
                    <h3 className="font-bold text-gray-800 mb-3 text-lg">Secure</h3>
                    <p className="text-gray-600">Your data is encrypted and protected</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200 hover:shadow-xl transition-all">
                    <div className="text-4xl mb-4">🎯</div>
                    <h3 className="font-bold text-gray-800 mb-3 text-lg">Easy to Use</h3>
                    <p className="text-gray-600">Intuitive interface for your team</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Company Information */}
            {currentStep === 2 && (
              <div className="animate-fade-in">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Company Information</h2>
                <p className="text-gray-600 mb-8">Tell us about your construction business</p>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="e.g., BuildRight Construction Ltd"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Email *
                      </label>
                      <input
                        type="email"
                        name="company_email"
                        value={formData.company_email}
                        onChange={handleInputChange}
                        onBlur={(e) => handleEmailBlur('organisation', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                          !companyEmailValidation.valid ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="info@company.com"
                      />
                      {companyEmailValidation.checking && (
                        <p className="text-gray-500 text-sm mt-1">Checking availability...</p>
                      )}
                      {!companyEmailValidation.checking && !companyEmailValidation.valid && (
                        <p className="text-red-500 text-sm mt-1">{companyEmailValidation.message}</p>
                      )}
                      {!companyEmailValidation.checking && companyEmailValidation.valid && companyEmailValidation.message && (
                        <p className="text-green-600 text-sm mt-1">✓ {companyEmailValidation.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Phone *
                      </label>
                      <input
                        type="tel"
                        name="company_phone"
                        value={formData.company_phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="+44 20 1234 5678"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="London"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Postcode *
                      </label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="SW1A 1AA"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        {countries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Organisation Type *
                      </label>
                      <select
                        name="organisation_type"
                        value={formData.organisation_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        {organisationTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Category *
                      </label>
                      <select
                        name="business_category"
                        value={formData.business_category}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        {[...businessCategories].sort((a, b) => a.localeCompare(b)).map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* DEV: Auto-fill button - only shown in development */}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        company_name: 'Test Construction Ltd',
                        company_email: `test.company.${Date.now()}@example.com`,
                        company_phone: '+44 20 7946 0958',
                        street_address: '123 Builder Street',
                        city: 'London',
                        postcode: 'EC1A 1BB',
                        country: 'United Kingdom',
                        organisation_type: 'Limited Company',
                        business_category: 'General Construction'
                      }));
                    }}
                    className="w-full bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-yellow-700 transition-all text-sm"
                    data-testid="dev-autofill-company-btn"
                  >
                    🔧 DEV: Auto-fill Company Info
                  </button>
                )}
              </div>
            )}
            
            {/* Step 3: Admin User Details */}
            {currentStep === 3 && (
              <div className="animate-fade-in">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Admin Account</h2>
                <p className="text-gray-600 mb-8">Create your administrator account</p>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="admin_first_name"
                        value={formData.admin_first_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="John"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="admin_last_name"
                        value={formData.admin_last_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Smith"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Email *
                    </label>
                    <input
                      type="email"
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                      onBlur={(e) => handleEmailBlur('admin', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        !adminEmailValidation.valid ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="john.smith@company.com"
                    />
                    {adminEmailValidation.checking && (
                      <p className="text-gray-500 text-sm mt-1">Checking availability...</p>
                    )}
                    {!adminEmailValidation.checking && !adminEmailValidation.valid && (
                      <p className="text-red-500 text-sm mt-1">{adminEmailValidation.message}</p>
                    )}
                    {!adminEmailValidation.checking && adminEmailValidation.valid && adminEmailValidation.message && (
                      <p className="text-green-600 text-sm mt-1">✓ {adminEmailValidation.message}</p>
                    )}
                    {!emailValidation.valid && (
                      <p className="text-red-500 text-sm mt-1">{emailValidation.message}</p>
                    )}
                  </div>

                  {/* Admin Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="admin_phone_number"
                      value={formData.admin_phone_number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="+44 7700 900000"
                      required
                    />
                  </div>

                  {/* Admin Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="admin_address"
                      value={formData.admin_address}
                      onChange={handleInputChange}
                      rows="2"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                      placeholder="123 Main Street, London, UK, SW1A 1AA"
                      required
                    />
                  </div>

                  {/* Admin Date of Birth */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      selected={formData.admin_date_of_birth ? new Date(formData.admin_date_of_birth) : null}
                      onChange={(date) => {
                        if (date) {
                          const formatted = date.toISOString().split('T')[0];
                          setFormData(prev => ({ ...prev, admin_date_of_birth: formatted }));
                        } else {
                          setFormData(prev => ({ ...prev, admin_date_of_birth: '' }));
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      maxDate={new Date()}
                      minDate={new Date('1920-01-01')}
                      placeholderText="Select date of birth"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      wrapperClassName="w-full"
                      required
                    />
                  </div>
                  
                  {/* Username Auto-Generation Info */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value="Will be auto-generated"
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Format: firstinitial.lastname (e.g., j.smith)
                    </p>
                  </div>
                </div>
                
{/* DEV: Auto-fill button - only shown in development */}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        admin_first_name: 'John',
                        admin_last_name: 'Smith',
                        admin_email: `john.smith.${Date.now()}@example.com`,
                        admin_phone_number: '+44 7700 900123',
                        admin_address: '456 Admin Lane, London, UK, SW1A 2AA',
                        admin_date_of_birth: '1985-06-15'
                      }));
                    }}
                    className="w-full bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-yellow-700 transition-all text-sm"
                    data-testid="dev-autofill-admin-btn"
                  >
                    🔧 DEV: Auto-fill Admin Details
                  </button>
                )}

                {/* Password Setup Info Box */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h5 className="text-sm font-medium text-blue-800">Secure Password Setup</h5>
                      <p className="text-sm text-blue-700 mt-1">
                        After registration, you will receive an email with a secure link to set your password. 
                        This link is valid for 48 hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 4: Trial Information */}
            {currentStep === 4 && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Your 14-Day Pro Trial</h2>
                <p className="text-gray-600 mb-6">Experience the full power of SiteOrbit</p>
                
                {/* Pro Plan Card */}
                <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white mb-6 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-sm font-medium text-purple-200">Selected Plan</span>
                      <h3 className="text-3xl font-bold">PRO</h3>
                    </div>
                    <div className="bg-white/20 rounded-full px-4 py-2">
                      <span className="text-sm font-semibold">14 Days Free</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Unlimited Sites</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Unlimited Team Members</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>All Modules Included</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Neo AI Assistant</span>
                    </div>
                  </div>
                </div>
                
                {/* Explanatory Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                  <div className="flex gap-3">
                    <div className="text-blue-500 mt-0.5">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-1">Why Pro?</h4>
                      <p className="text-sm text-blue-700 leading-relaxed">
                        All new registrations start with a <strong>14-day Pro trial</strong> so you can experience the full 
                        platform features. During this period, explore everything SiteOrbit has to offer and then choose 
                        the plan that best suits your needs. No payment required until you&apos;re ready to subscribe.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 5: Review & Submit */}
            {currentStep === 5 && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Review & Submit</h2>
                <p className="text-gray-600 mb-6">Please review your information before submitting</p>
                
                {/* Company Information Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-lg">🏢</span> Company Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Company Name:</span>
                      <p className="font-medium text-gray-800">{formData.company_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium text-gray-800">{formData.company_email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Phone:</span>
                      <p className="font-medium text-gray-800">{formData.company_phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Organisation Type:</span>
                      <p className="font-medium text-gray-800">{formData.organisation_type}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-gray-500">Address:</span>
                      <p className="font-medium text-gray-800">
                        {formData.street_address}, {formData.city}, {formData.postcode}, {formData.country}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Business Category:</span>
                      <p className="font-medium text-gray-800">{formData.business_category}</p>
                    </div>
                  </div>
                </div>
                
                {/* Admin Account Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-lg">👤</span> Admin Account
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <p className="font-medium text-gray-800">{formData.admin_first_name} {formData.admin_last_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium text-gray-800">{formData.admin_email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Phone:</span>
                      <p className="font-medium text-gray-800">{formData.admin_phone_number}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Date of Birth:</span>
                      <p className="font-medium text-gray-800">{formData.admin_date_of_birth}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-gray-500">Address:</span>
                      <p className="font-medium text-gray-800">{formData.admin_address}</p>
                    </div>
                  </div>
                </div>
                
                {/* Subscription Plan Summary */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-lg">⭐</span> Subscription Plan
                  </h3>
                  <div>
                    <p className="text-xl font-bold text-purple-700">PRO</p>
                    <p className="text-sm text-gray-600 mb-2">14-day free trial</p>
                    <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      No Payment / Card details required
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 6: Success */}
            {currentStep === 6 && success && (
              <div className="text-center animate-fade-in">
                <div className="text-6xl mb-6">🎉</div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Registration Successful!
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  Welcome to SiteOrbit, {formData.admin_first_name}!
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📧</div>
                    <div>
                      <h3 className="font-bold text-blue-800 mb-2">Check Your Email to Set Your Password</h3>
                      <p className="text-blue-700 text-sm leading-relaxed">
                        We&apos;ve sent an email to <strong>{formData.admin_email}</strong> with a secure link to set your password.
                        Please click the link in the email to complete your account setup. The link is valid for 48 hours.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6 text-left">
                  <h3 className="font-bold text-indigo-800 mb-3">Your Registration Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium text-gray-800">{formData.company_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Admin Email:</span>
                      <span className="font-medium text-gray-800">{formData.admin_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Organisation ID:</span>
                      <span className="font-mono font-bold text-indigo-600">{organisationId}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6 text-left">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔑</div>
                    <div>
                      <h3 className="font-bold text-purple-800 mb-2">Your Login Credentials</h3>
                      <p className="text-purple-700 text-sm mb-2">
                        Your username has been automatically generated:
                      </p>
                      <div className="bg-white border border-purple-300 rounded-lg px-4 py-2 inline-block">
                        <span className="font-mono font-bold text-purple-600 text-lg">{generatedUsername}</span>
                      </div>
                      <p className="text-purple-600 text-xs mt-2">
                        Use this username along with the password you set via email to log in once your account is activated.
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-8">
                  If you have any questions, please contact our support team.
                </p>
                
                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:shadow-lg transition-all"
                >
                  Return to Homepage
                </button>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </div>
          
          {/* Navigation Buttons - Full Width */}
          {currentStep < 6 && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-12 py-8 flex justify-between items-center border-t-2 border-gray-200">
              {currentStep > 1 ? (
                <button
                  onClick={prevStep}
                  className="flex items-center space-x-2 px-8 py-4 text-gray-700 font-semibold hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Back</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center space-x-2 px-8 py-4 text-gray-700 font-semibold hover:text-gray-900 hover:bg-white rounded-xl transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
              )}
              
              {currentStep < 5 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <span>Continue</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : currentStep === 5 ? (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center space-x-2 px-10 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{loading ? 'Submitting...' : 'Complete Registration'}</span>
                  <span className="text-xl">🎉</span>
                </button>
              ) : null}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganisationRegistration;
